import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Save } from "lucide-react";
import ImagePicker from "@/components/admin/ImagePicker";
import { TextField, TextArea, PageHeader } from "@/components/admin/Form";
import RichTextEditor from "@/components/admin/RichTextEditor";
import { fileUrl } from "@/lib/api";

const EMPTY = { title: "", slug: "", excerpt: "", cover_image: "", cover_caption: "", category: "Stories", tags: [], author: "Cafe Hideout", body: "", published: true };

export default function BlogEditor() {
    const [posts, setPosts] = useState([]);
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/blog");
            setPosts(data);
        } catch {
            toast.error("Failed to load posts");
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const save = async () => {
        if (!editing.title || !editing.slug) {
            toast.error("Title and slug are required");
            return;
        }
        setSaving(true);
        try {
            if (editing.id) {
                await api.put(`/admin/blog/${editing.id}`, editing);
                toast.success("Post updated.");
            } else {
                await api.post("/admin/blog", editing);
                toast.success("Post created.");
            }
            setEditing(null);
            await load();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Save failed");
        } finally { setSaving(false); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this post?")) return;
        try {
            await api.delete(`/admin/blog/${id}`);
            toast.success("Deleted.");
            await load();
        } catch { toast.error("Delete failed"); }
    };

    return (
        <div data-testid="blog-editor">
            <PageHeader subtitle="Blog" title="Manage stories" />

            <button
                onClick={() => setEditing({ ...EMPTY })}
                className="btn-pill btn-pill-coffee mb-8"
                data-testid="new-post-button"
            >
                <Plus size={14} /> New Story
            </button>

            {loading ? (
                <div className="tracked text-coffee/50 text-[11px]">Loading…</div>
            ) : (
                <div className="space-y-3">
                    {posts.map((p) => (
                        <div key={p.id} className="bg-white border border-coffee/10 p-5 flex gap-4 items-center" data-testid={`post-row-${p.slug}`}>
                            <div className="w-16 h-16 bg-coffee/5 overflow-hidden flex-shrink-0">
                                {p.cover_image && <img src={fileUrl(p.cover_image)} alt="" className="w-full h-full object-cover" />}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-mono tracked text-coffee/40 text-[9px]">{p.category}</div>
                                <div className="font-editorial font-bold text-xl text-coffee truncate">{p.title}</div>
                                <div className="text-coffee/60 text-xs truncate">/{p.slug}</div>
                            </div>
                            <button onClick={() => setEditing(p)} className="text-coffee/70 hover:text-coffee p-2" data-testid={`edit-${p.slug}`}>
                                <Pencil size={16} />
                            </button>
                            <button onClick={() => remove(p.id)} className="text-coffee/50 hover:text-destructive p-2" data-testid={`delete-${p.slug}`}>
                                <Trash2 size={16} />
                            </button>
                        </div>
                    ))}
                    {posts.length === 0 && (
                        <div className="font-editorial font-bold text-coffee/50 text-xl">No stories yet. Write the first one.</div>
                    )}
                </div>
            )}

            {editing && (
                <div className="fixed inset-0 bg-coffee/60 z-40 flex items-stretch md:items-center justify-center p-0 md:p-8" onClick={() => setEditing(null)}>
                    <div className="bg-cream-light w-full md:max-w-3xl md:rounded-lg overflow-y-auto max-h-screen" onClick={(e) => e.stopPropagation()}>
                        <div className="sticky top-0 bg-cream-light border-b border-coffee/15 px-6 py-4 flex items-center justify-between z-10">
                            <h3 className="font-editorial font-bold text-coffee text-2xl">
                                {editing.id ? "Edit Story" : "New Story"}
                            </h3>
                            <button onClick={() => setEditing(null)} aria-label="close"><X size={20} /></button>
                        </div>
                        <div className="p-6 space-y-5">
                            <TextField label="Title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v, slug: editing.id ? editing.slug : v.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") })} testid="post-title" />
                            <TextField label="Slug" value={editing.slug} onChange={(v) => setEditing({ ...editing, slug: v })} testid="post-slug" />
                            <TextField label="Category" value={editing.category} onChange={(v) => setEditing({ ...editing, category: v })} testid="post-category" />
                            <TextField
                                label="Tags (comma-separated, e.g., Craft, Community, Food)"
                                value={(editing.tags || []).join(", ")}
                                onChange={(v) => setEditing({ ...editing, tags: v.split(",").map((s) => s.trim()).filter(Boolean) })}
                                testid="post-tags"
                            />
                            <TextField label="Author" value={editing.author} onChange={(v) => setEditing({ ...editing, author: v })} testid="post-author" />
                            <ImagePicker label="Cover Image" value={editing.cover_image} onChange={(v) => setEditing({ ...editing, cover_image: v })} testid="post-cover" hint="Recommended: 1200 × 800 px (3:2 landscape) · JPG · under 600 KB" />
                            <TextField label="Cover Caption (optional italic caption under hero image)" value={editing.cover_caption} onChange={(v) => setEditing({ ...editing, cover_caption: v })} testid="post-cover-caption" />
                            <TextArea label="Excerpt" rows={2} value={editing.excerpt} onChange={(v) => setEditing({ ...editing, excerpt: v })} testid="post-excerpt" />
                            <div>
                                <label className="tracked text-coffee/60 text-[10px] block mb-3">Body</label>
                                <RichTextEditor value={editing.body} onChange={(v) => setEditing({ ...editing, body: v })} testid="post-body" />
                            </div>
                            <label className="flex items-center gap-2 text-sm text-coffee">
                                <input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} data-testid="post-published" />
                                Published
                            </label>
                        </div>
                        <div className="sticky bottom-0 bg-cream-light border-t border-coffee/15 px-6 py-4 flex gap-3 justify-end">
                            <button onClick={() => setEditing(null)} className="tracked text-[10px] text-coffee">Cancel</button>
                            <button onClick={save} disabled={saving} className="btn-pill btn-pill-coffee" data-testid="save-post">
                                <Save size={14} /> {saving ? "Saving…" : "Save Story"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
